import React, { useState, useRef, useEffect } from "react";
import { db } from "../firebase";
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { Alert } from "./alert";
import { Button } from "./button";
import { showAlert } from "../alertService";
import { backendFetch } from "../utils/backend";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { esSoloLectura } from "../utils/roles";
import {
  PaperAirplaneIcon,
  MegaphoneIcon,
  TrashIcon,
  ClipboardIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

// Username del bot SIN @ (el de BotFather). Override por .env.local si el
// handle de producción cambia.
const BOT_USERNAME =
  process.env.REACT_APP_TELEGRAM_BOT_USERNAME || "VenueboardOwnersBot";

const CODE_TTL_MINUTES = 30;
const CODE_CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

const SCOPES = [
  { key: "calendario", label: "Calendario", hint: "/disponibilidad" },
  { key: "leads", label: "Leads", hint: "/leads" },
  { key: "cuentas", label: "Cuentas", hint: "/saldo" },
];

// Renderiza el subset de markdown que el backend convierte a HTML de Telegram
// (**negritas**, *cursivas*, `código`) para que el simulador se vea como el
// grupo real. Sin dangerouslySetInnerHTML — puro split a elementos React.
const renderMarkdownLite = (text) => {
  const tokens = String(text || "").split(
    /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g,
  );
  return tokens.map((seg, i) => {
    if (!seg) return null;
    if (seg.startsWith("**") && seg.endsWith("**") && seg.length > 4) {
      return <strong key={i}>{seg.slice(2, -2)}</strong>;
    }
    if (seg.startsWith("`") && seg.endsWith("`") && seg.length > 2) {
      return (
        <code key={i} className="text-[0.85em]">
          {seg.slice(1, -1)}
        </code>
      );
    }
    if (seg.startsWith("*") && seg.endsWith("*") && seg.length > 2) {
      return <em key={i}>{seg.slice(1, -1)}</em>;
    }
    return seg;
  });
};

const generateCode = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_CHARSET[b % CODE_CHARSET.length]).join(
    "",
  );
};

// Simulador de chat (solo admins): ejecuta el flujo real del bot en el
// backend (Firestore + Gemini) sin pasar por Telegram, para probar comandos,
// lenguaje natural y permisos apagados antes de soltar el bot a un grupo real.
// Los mensajes NL sí consumen cuota del LLM.
const SimuladorChat = ({ venueId }) => {
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [simScopes, setSimScopes] = useState({
    calendario: true,
    leads: true,
    cuentas: true,
  });
  const [enviando, setEnviando] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [mensajes, enviando]);

  const enviar = async () => {
    const t = texto.trim();
    if (!t || enviando || !venueId) return;
    setTexto("");
    setMensajes((m) => [...m, { from: "user", text: t }]);
    setEnviando(true);
    try {
      const resp = await backendFetch("/telegram/simulate", {
        method: "POST",
        body: {
          venueId,
          text: t,
          scopes: simScopes,
          // Historial para que el bot resuelva referencias («¿ya está pagado?»)
          history: mensajes
            .filter((m) => !m.error)
            .slice(-8)
            .map((m) => ({ from: m.from, text: m.text })),
        },
      });
      setMensajes((m) => [
        ...m,
        { from: "bot", ...(resp?.message || {}), tokens: resp?.tokens },
      ]);
    } catch (err) {
      console.error("Simulador telegram error:", err);
      setMensajes((m) => [
        ...m,
        {
          from: "bot",
          text: `⚠️ ${err?.message || "Error del simulador"}`,
          error: true,
        },
      ]);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mt-4">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-1">
        Simulador del bot{" "}
        <span className="text-xs font-normal text-zinc-400">(solo admins)</span>
      </h3>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
        Ejecuta el flujo real del bot contra este venue sin pasar por Telegram.
        Los permisos de abajo simulan los scopes del grupo; los mensajes en
        lenguaje natural consumen cuota del LLM.
      </p>

      <div className="flex items-center gap-4 mb-3">
        {SCOPES.map((s) => (
          <label
            key={s.key}
            className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-zinc-300"
              style={{ accentColor: ACCENT }}
              checked={simScopes[s.key]}
              onChange={() =>
                setSimScopes((prev) => ({ ...prev, [s.key]: !prev[s.key] }))
              }
            />
            {s.label}
          </label>
        ))}
        <button
          type="button"
          onClick={() => setMensajes([])}
          disabled={enviando || mensajes.length === 0}
          className="ml-auto text-xs text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 underline disabled:opacity-50 disabled:no-underline"
          title="Borra la conversación y el contexto que recibe el bot"
        >
          Limpiar chat
        </button>
      </div>

      <div className="h-72 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-900/40 p-3 mb-3 space-y-2">
        {mensajes.length === 0 && (
          <p className="text-xs text-zinc-400 text-center mt-24">
            Escribe /ayuda o una pregunta en lenguaje natural para empezar.
          </p>
        )}
        {mensajes.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className="max-w-[85%]">
              <div
                className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  m.from === "user"
                    ? "text-white"
                    : m.error
                      ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                      : "bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700/60"
                }`}
                style={m.from === "user" ? { backgroundColor: ACCENT } : undefined}
              >
                {m.from === "bot" && !m.error
                  ? renderMarkdownLite(m.text)
                  : m.text}
              </div>
              {m.from === "bot" && !m.error && m.toolCalls?.length > 0 && (
                <div className="mt-1 px-1 space-y-0.5">
                  {m.toolCalls.map((tc, j) => (
                    <details key={j} className="text-[10px] text-zinc-400">
                      <summary className="cursor-pointer font-mono truncate">
                        🔧 {tc.tool}({JSON.stringify(tc.args)})
                      </summary>
                      <pre className="mt-0.5 max-h-32 overflow-y-auto whitespace-pre-wrap rounded bg-zinc-100 dark:bg-zinc-900/60 p-1.5 text-[10px] text-zinc-500 dark:text-zinc-400">
                        {tc.resultado}
                      </pre>
                    </details>
                  ))}
                </div>
              )}
              {m.from === "bot" && !m.error && m.chars !== undefined && (
                <div className="text-[10px] text-zinc-400 mt-0.5 px-1">
                  {m.chars.toLocaleString()} caracteres
                  {m.tokens
                    ? ` · ${m.tokens.input}/${m.tokens.output} tokens`
                    : ""}
                  {m.tooLong && (
                    <span className="text-red-500 font-semibold">
                      {" "}
                      · ⚠️ Telegram lo rechazaría (límite 4,096)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {enviando && (
          <div className="text-xs text-zinc-400 px-1">el bot está escribiendo…</div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") enviar();
          }}
          placeholder="/saldo, /disponibilidad, o «¿está libre el sábado?»"
          disabled={enviando}
        />
        <button
          type="button"
          onClick={enviar}
          disabled={enviando || !texto.trim()}
          className="flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          Enviar
        </button>
      </div>
    </div>
  );
};

const TelegramManager = () => {
  const { selectedVenueId, telegramChats, telegramChatsLoading, venueFeatures } =
    useVenue();
  const { user, userInfo } = useAuth();
  // Inversionista (P-FRA-13): ve qué grupos están vinculados, qué puede
  // consultar cada uno y su historial. No conecta grupos (generar un código
  // es escribir en telegramLinkCodes, que además no puede leer), no cambia
  // permisos, no identifica (pega al backend con autoridad de owner) ni
  // desvincula.
  const soloLectura = esSoloLectura(userInfo);
  const [linkPayload, setLinkPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unlinkCandidate, setUnlinkCandidate] = useState(null);
  const [historialChat, setHistorialChat] = useState(null);
  const [historialMensajes, setHistorialMensajes] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);

  // Historial de preguntas/respuestas del grupo seleccionado (escrito por el
  // backend en cada mensaje procesado; aquí solo se lee).
  useEffect(() => {
    if (!historialChat || !selectedVenueId) {
      setHistorialMensajes([]);
      return;
    }
    setHistorialLoading(true);
    const q = query(
      collection(
        db,
        "venues",
        selectedVenueId,
        "telegramChats",
        historialChat.id,
        "mensajes",
      ),
      orderBy("at", "desc"),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setHistorialMensajes(
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
        );
        setHistorialLoading(false);
      },
      (err) => {
        console.error("Historial telegram snapshot error:", err);
        setHistorialLoading(false);
      },
    );
    return unsub;
  }, [historialChat, selectedVenueId]);

  if (venueFeatures?.telegram !== true) return null;

  const activeChats = telegramChats
    .filter((c) => c.active)
    .slice()
    .sort((a, b) => (a.title || "").localeCompare(b.title || "", "es"));

  const handleGenerateLink = async () => {
    if (!selectedVenueId) return;
    setBusy(true);
    try {
      const code = generateCode();
      await setDoc(doc(db, "venues", selectedVenueId, "telegramLinkCodes", code), {
        createdByUid: user?.uid || "",
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromMillis(
          Date.now() + CODE_TTL_MINUTES * 60 * 1000,
        ),
        used: false,
      });
      const payload = `${selectedVenueId}__${code}`;
      setLinkPayload(payload);
      window.open(
        `https://t.me/${BOT_USERNAME}?startgroup=${payload}`,
        "_blank",
        "noopener",
      );
    } catch (err) {
      console.error("Error generating telegram link code:", err);
      showAlert("No se pudo generar el código de vinculación.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleCopyPayload = async () => {
    if (!linkPayload) return;
    try {
      await navigator.clipboard.writeText(`/vincular ${linkPayload}`);
      showAlert("Comando copiado al portapapeles.", "success");
    } catch {
      showAlert("No se pudo copiar — selecciona el texto manualmente.", "warning");
    }
  };

  const handleToggleScope = async (chat, scopeKey) => {
    const next = !chat.scopes?.[scopeKey];
    try {
      await updateDoc(
        doc(db, "venues", selectedVenueId, "telegramChats", chat.id),
        { [`scopes.${scopeKey}`]: next },
      );
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.TELEGRAM_TOGGLE_SCOPE,
          entity: "telegram_chat",
          entityId: chat.id,
          entityName: chat.title || "",
          details: `${scopeKey}: ${next ? "activado" : "desactivado"}`,
        },
        userInfo,
        user,
      );
    } catch (err) {
      console.error("Error toggling telegram scope:", err);
      showAlert("No se pudo actualizar el permiso.", "error");
    }
  };

  const handleIdentify = async (chat) => {
    setBusy(true);
    try {
      await backendFetch(
        `/telegram/venues/${selectedVenueId}/chats/${chat.id}/identify`,
        { method: "POST" },
      );
      showAlert("El bot envió un mensaje al grupo para identificarlo.", "success");
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.TELEGRAM_IDENTIFY_GROUP,
          entity: "telegram_chat",
          entityId: chat.id,
          entityName: chat.title || "",
          details: "Mensaje de identificación enviado al grupo",
        },
        userInfo,
        user,
      );
    } catch (err) {
      console.error("Error identifying telegram chat:", err);
      showAlert(
        err?.message || "No se pudo enviar el mensaje al grupo.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmUnlink = async () => {
    const chat = unlinkCandidate;
    if (!chat || !selectedVenueId) {
      setUnlinkCandidate(null);
      return;
    }
    setBusy(true);
    try {
      await deleteDoc(doc(db, "venues", selectedVenueId, "telegramChats", chat.id));
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.TELEGRAM_UNLINK_GROUP,
          entity: "telegram_chat",
          entityId: chat.id,
          entityName: chat.title || "",
          details: "Grupo desvinculado desde la app",
        },
        userInfo,
        user,
      );
    } catch (err) {
      console.error("Error unlinking telegram chat:", err);
      showAlert("No se pudo desvincular el grupo.", "error");
    } finally {
      setBusy(false);
      setUnlinkCandidate(null);
    }
  };

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-1">
        Telegram
      </h2>
      <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
        {soloLectura
          ? "Grupos de Telegram vinculados a este venue y la información que cada uno puede consultar con el bot."
          : "Conecta grupos de Telegram con tu venue para que tu equipo consulte disponibilidad, leads y saldos desde el chat — por comandos o preguntándole al bot en lenguaje natural."}
      </p>

      {/* El tutorial y el botón de conectar solo sirven a quien vincula: en
          solo lectura se ocultan en vez de apagarse. */}
      {!soloLectura && (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mb-4">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-2">
          Conectar un grupo
        </h3>
        <ol className="list-decimal list-inside text-sm text-zinc-600 dark:text-zinc-300 space-y-1 mb-3">
          <li>
            Si no tienes Telegram, descárgalo en{" "}
            <a
              href="https://telegram.org/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: ACCENT }}
            >
              telegram.org/apps
            </a>
            .
          </li>
          <li>
            Presiona <span className="font-medium">Conectar un grupo</span>:
            Telegram te pedirá elegir (o crear) el grupo y agregará al bot con
            el código incluido.
          </li>
          <li>El grupo aparecerá aquí abajo en cuanto quede vinculado.</li>
        </ol>
        <button
          type="button"
          onClick={handleGenerateLink}
          disabled={busy}
          className="flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          Conectar un grupo de Telegram
        </button>

        {linkPayload && (
          <div className="mt-3 rounded-lg bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-700/60 p-3 text-sm">
            <p className="text-zinc-600 dark:text-zinc-300 mb-2">
              ¿No se abrió Telegram, o prefieres hacerlo manual? Agrega{" "}
              <span className="font-mono font-medium">@{BOT_USERNAME}</span> a tu
              grupo y pega ahí este comando (válido {CODE_TTL_MINUTES} minutos,
              un solo uso):
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs">
                /vincular {linkPayload}
              </code>
              <button
                type="button"
                onClick={handleCopyPayload}
                className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1.5"
                title="Copiar comando"
                aria-label="Copiar comando de vinculación"
              >
                <ClipboardIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-3">
          Cualquier miembro del grupo podrá consultar lo que habilites aquí,
          incluidos saldos si activas Cuentas. El bot puede leer los mensajes
          del grupo (requisito de Telegram para entender lenguaje natural),
          pero solo procesa y responde los que lo mencionan o le responden.
        </p>
      </div>
      )}

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
        {telegramChatsLoading ? (
          <div className="text-center py-10 text-zinc-400">Cargando...</div>
        ) : activeChats.length === 0 ? (
          <div className="text-center py-10 text-sm text-zinc-400">
            Todavía no hay grupos vinculados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700/60">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Grupo
                  </th>
                  {SCOPES.map((s) => (
                    <th
                      key={s.key}
                      className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-zinc-500"
                    >
                      {s.label}
                    </th>
                  ))}
                  <th className="px-3 py-2.5 w-[110px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {activeChats.map((chat) => (
                  <tr key={chat.id}>
                    <td className="px-4 py-2.5 text-zinc-700 dark:text-zinc-200">
                      {chat.title || `Grupo ${chat.id}`}
                    </td>
                    {SCOPES.map((s) => (
                      <td key={s.key} className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          className={`h-4 w-4 rounded border-zinc-300 ${
                            soloLectura ? "cursor-default" : "cursor-pointer"
                          }`}
                          style={{ accentColor: ACCENT }}
                          checked={chat.scopes?.[s.key] === true}
                          disabled={soloLectura}
                          onChange={() => handleToggleScope(chat, s.key)}
                          title={`${s.label} (${s.hint})`}
                          aria-label={`${s.label} para ${chat.title || chat.id}`}
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() =>
                          setHistorialChat(
                            historialChat?.id === chat.id ? null : chat,
                          )
                        }
                        className={`p-1.5 ${
                          historialChat?.id === chat.id
                            ? "text-zinc-700 dark:text-zinc-200"
                            : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                        }`}
                        title="Historial — preguntas y respuestas del bot en este grupo"
                        aria-label="Ver historial del grupo"
                      >
                        <ClockIcon className="h-4 w-4" />
                      </button>
                      {!soloLectura && (
                      <>
                      <button
                        type="button"
                        onClick={() => handleIdentify(chat)}
                        disabled={busy}
                        className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1.5 disabled:opacity-50"
                        title="Identificar — el bot envía un mensaje a este grupo"
                        aria-label="Identificar grupo"
                      >
                        <MegaphoneIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setUnlinkCandidate(chat)}
                        disabled={busy}
                        className="text-zinc-400 hover:text-red-600 p-1.5 disabled:opacity-50"
                        title="Desvincular grupo"
                        aria-label="Desvincular grupo"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {historialChat && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              Historial — {historialChat.title || `Grupo ${historialChat.id}`}
              <span className="ml-2 text-xs font-normal text-zinc-400">
                últimos {historialMensajes.length} mensajes
              </span>
            </h3>
            <button
              type="button"
              onClick={() => setHistorialChat(null)}
              className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1"
              aria-label="Cerrar historial"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          {historialLoading ? (
            <div className="text-center py-8 text-zinc-400 text-sm">
              Cargando...
            </div>
          ) : historialMensajes.length === 0 ? (
            <div className="text-center py-8 text-sm text-zinc-400">
              Este grupo todavía no le ha preguntado nada al bot.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-3">
              {historialMensajes.map((m) => (
                <div
                  key={m.id}
                  className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3"
                >
                  <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-1.5">
                    <span className="font-medium text-zinc-500 dark:text-zinc-300">
                      {m.autor || "Alguien del grupo"}
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        m.tipo === "comando"
                          ? "bg-zinc-100 dark:bg-zinc-700/60 text-zinc-500 dark:text-zinc-300"
                          : "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                      }`}
                    >
                      {m.tipo === "comando" ? "comando" : "pregunta"}
                    </span>
                    <span className="ml-auto">
                      {m.at?.toDate
                        ? m.at.toDate().toLocaleString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100 mb-1">
                    {m.pregunta}
                  </p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-300 whitespace-pre-wrap">
                    {m.respuesta}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {userInfo?.role === "admin" && <SimuladorChat venueId={selectedVenueId} />}

      <Alert open={!!unlinkCandidate} onClose={() => setUnlinkCandidate(null)}>
        <div className="font-semibold mb-2">
          ¿Desvincular "{unlinkCandidate?.title || unlinkCandidate?.id}"?
        </div>
        <div className="text-sm text-zinc-600 mb-4">
          El bot dejará de responder en ese grupo. Puedes volver a vincularlo
          cuando quieras generando un código nuevo.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setUnlinkCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleConfirmUnlink}>
            Desvincular
          </Button>
        </div>
      </Alert>
    </div>
  );
};

export default TelegramManager;
